import { Box, Link, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Alert from '/@/components/bayer/alert';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useFieldById, useMyFields } from '/@/hooks/fields';
import { _ } from '/@/locale';
import {
    setCalculationResult,
    setCampaignFrom,
    setCampaignTo,
    setClay,
    setDensity,
    setOrganicMaterial,
    setSelectedCampaigns,
    setSoiltype,
} from '/@/store/carbon-calculator/actions';
import {
    getAllSoilTypesOptions,
    getCalculationResult,
    getCampaignFrom,
    getCampaignTo,
    getClay,
    getCrops,
    getDensity,
    getOrganicMaterial,
    getSelectedCampaigns,
    getSelectedFarmingOption,
    getSoilType,
} from '/@/store/carbon-calculator/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import { seedRecommendationUrl } from '/@/views/recommendations/seed-recommendation-route';
import NewFieldGrowersModal from '../dashboard/growers/components/new-field-modal';
import intaLogo from './assets/inta.png';
import CampaignsCard from './components/campaigns-card';
import FieldForm from './components/field-form';
import ResultSnackbar from './components/result-snackbar';
import ResultsCard from './components/results-card';
import { useCarbonCalculation, useCarbonCalculatorCatalogs } from './hooks';
import { areInputsValid, campaignsInRange, type SeasonResult, type SelectedCampaigns } from './utils';

interface Field {
    id: number;
    name: string;
    location: string;
}

interface SoilType {
    label: string;
    clay: number;
    organicMaterial: number;
    apparentDensity: number;
}

interface CarbonCalculatorProps {
    fields: Field[];
    fieldZoneIds?: string;
    selectedFieldId: number;
    setSelectedFieldId: (fieldId: number) => void;
    userNodeId?: number;
}

const CAMPAIGNS_SECTION_ID = 'carbon-calculator-campaigns';

const scrollIntoView = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
};

function CarbonCalculator({
    fields,
    fieldZoneIds,
    selectedFieldId,
    setSelectedFieldId,
    userNodeId,
}: CarbonCalculatorProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const campaignFrom = useSelector(getCampaignFrom);
    const campaignTo = useSelector(getCampaignTo);
    const selectedCampaigns: SelectedCampaigns = useSelector(getSelectedCampaigns);
    const farmingType = useSelector(getSelectedFarmingOption);
    const allSoilTypes: SoilType[] = useSelector(getAllSoilTypesOptions);
    const soilType = useSelector(getSoilType);
    const crops = useSelector(getCrops);
    const clay = useSelector(getClay);
    const organicMaterial = useSelector(getOrganicMaterial);
    const density = useSelector(getDensity);
    const calculationResult: SeasonResult[] = useSelector(getCalculationResult);

    const [temperature, setTemperature] = useState<number | string>(16.8);
    const [showCampaigns, setShowCampaigns] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState(0);
    const [crop, setCrop] = useState('');
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [showNewFieldModal, setShowNewFieldModal] = useState(false);

    const campaignsOptions = useCarbonCalculatorCatalogs(fieldZoneIds);
    const { calculate, isCalculating, hasError } = useCarbonCalculation(temperature);

    const debounceSnackbar = useMemo(() => debounce(() => setShowSnackbar(true), 600), []);
    const debounceScroll = useMemo(() => debounce((id: string) => scrollIntoView(id), 50), []);
    useEffect(
        () => () => {
            debounceSnackbar.cancel();
            debounceScroll.cancel();
        },
        [debounceSnackbar, debounceScroll]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: hide the snackbar 3s after each new result
    useEffect(() => {
        const timeout = setTimeout(() => setShowSnackbar(false), 3000);
        return () => clearTimeout(timeout);
    }, [calculationResult]);

    const handleZoneSelection = (fieldId: number) => {
        setSelectedFieldId(fieldId);
        setShowCampaigns(false);
        dispatch(setCalculationResult([]));
        dispatch(setSoiltype(undefined));
    };

    const handleCreateField = () => {
        if (settings.app.theme === 'expertobayer') {
            setShowNewFieldModal(true);
        } else {
            navigate(reverse('fields.NewField'));
        }
    };

    const setSoilTypeValue = (val: string) => {
        const soil = allSoilTypes?.find((s) => s.label === val);
        if (!soil) return;
        dispatch(setSoiltype(soil));
        dispatch(setOrganicMaterial(soil.organicMaterial));
        dispatch(setDensity(soil.apparentDensity));
        dispatch(setClay(soil.clay));
    };

    /**
     * Keeps the loaded campaigns that are still in range and adds the new ones. The results
     * belong to the old range, so they're cleared; the recalculation refills them.
     */
    const setCampaignRange = (from: number | undefined, to: number | undefined) => {
        dispatch(setCampaignFrom(from));
        dispatch(setCampaignTo(to));
        dispatch(setCalculationResult([]));
        setSelectedSeason(0);
        if (from === undefined || to === undefined) {
            dispatch(setSelectedCampaigns({}));
            setShowCampaigns(false);
            return;
        }
        dispatch(setSelectedCampaigns(campaignsInRange(selectedCampaigns, from, to, crops)));
    };

    const isFormComplete =
        campaignFrom !== undefined &&
        campaignTo !== undefined &&
        farmingType !== undefined &&
        !!soilType &&
        areInputsValid({ temperature, clay, organicMaterial, density });

    const handleLoadMore = () => {
        setShowErrors(true);
        if (!isFormComplete) return;
        setShowCampaigns(true);
        debounceScroll(CAMPAIGNS_SECTION_ID);
    };

    const selectedField = fields.find((field) => field.id === selectedFieldId);

    return (
        <PageContainer
            header={
                <Header
                    title={_('Simulador de rotaciones sustentables')}
                    subtitle={
                        <>
                            {_('Evalua la dinámica del suelo en tus rotaciones.')}{' '}
                            {_('Para ver más información, poder acceder a la ')}
                            <Link href='/library' target='_blank' rel='noreferrer' color='secondary'>
                                {_('biblioteca')}
                            </Link>
                        </>
                    }
                />
            }
        >
            <Stack spacing={4} mt={3}>
                <FieldForm
                    fields={fields}
                    selectedFieldId={selectedFieldId}
                    campaignsOptions={campaignsOptions}
                    temperature={temperature}
                    showErrors={showErrors}
                    onZoneSelection={handleZoneSelection}
                    onCreateField={handleCreateField}
                    onTempChange={setTemperature}
                    // A start at or after the end drops the end, which has to be picked again.
                    onCampaignFromChange={(from) => setCampaignRange(from, campaignTo > from ? campaignTo : undefined)}
                    onCampaignToChange={(to) => setCampaignRange(campaignFrom, to)}
                    onSoilTypeChange={setSoilTypeValue}
                    onClayChange={(value) => dispatch(setClay(value))}
                    onOrganicMaterialChange={(value) => dispatch(setOrganicMaterial(value))}
                    onDensityChange={(value) => dispatch(setDensity(value))}
                    onLoadMore={handleLoadMore}
                />

                <Box id={CAMPAIGNS_SECTION_ID} sx={{ scrollMarginTop: 16 }}>
                    {showCampaigns && campaignFrom !== undefined && campaignTo !== undefined && (
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems='flex-start'>
                            <Box flex={1} minWidth={0} width='100%'>
                                <CampaignsCard
                                    selectedSeason={selectedSeason}
                                    onSelectSeason={setSelectedSeason}
                                    onNextSeason={() => setSelectedSeason((prev) => prev + 1)}
                                    onCalculate={calculate}
                                    onCropSelected={setCrop}
                                    onYieldEntered={debounceSnackbar}
                                />
                            </Box>
                            <Box flex={1} minWidth={0} width='100%' position={{ md: 'sticky' }} top={{ md: 16 }}>
                                <ResultsCard
                                    isCalculating={isCalculating}
                                    hasError={hasError}
                                    onRetry={() => calculate()}
                                />
                            </Box>
                        </Stack>
                    )}
                </Box>

                {crop === 'corn' && selectedField && (
                    <Alert
                        severity='blue'
                        actions={[
                            {
                                kind: 'primary',
                                label: _('Pedir una recomendación'),
                                onClick: () =>
                                    navigate(
                                        seedRecommendationUrl({
                                            crop: 'corn',
                                            fieldId: selectedField.id,
                                            entrySource: 'carbon-calculator',
                                        })
                                    ),
                            },
                        ]}
                    >
                        {_('¡Tenés pensado sembrar maíz!, si querés podés pedir una recomendación.')}
                    </Alert>
                )}

                <Stack
                    direction='row'
                    spacing={2}
                    alignItems='center'
                    pt={3}
                    sx={{ borderTop: (t) => `1px solid ${t.palette.divider}` }}
                >
                    <Typography variant='caption' color='primary.mui.inactive' fontStyle='italic'>
                        {_(
                            'Esta herramienta es un desarrollo conjunto entre el Instituto Nacional de Tecnología Agropecuaria de Argentina y Bayer Crop Science.'
                        )}
                    </Typography>
                    <Box component='img' src={intaLogo} alt='INTA' sx={{ height: 32, flexShrink: 0 }} />
                </Stack>
            </Stack>

            <ResultSnackbar
                lastResult={calculationResult[calculationResult.length - 1]}
                open={isMobile && showSnackbar}
                onClose={() => setShowSnackbar(false)}
            />
            <NewFieldGrowersModal
                open={showNewFieldModal}
                setOpen={() => setShowNewFieldModal(false)}
                growerUserNodeId={userNodeId}
                trackEntrySource='carbon-calculator'
            />
        </PageContainer>
    );
}

const CarbonCalculatorWrapper = (props: { fieldId: number }) => {
    const [selectedFieldId, setSelectedFieldId] = useState(props.fieldId);
    const country = useSelector(getSessionCountry);

    const fieldQuery = useFieldById({ id: selectedFieldId });
    const userNodeId = fieldQuery.data?.userNode;
    const fieldsQuery = useMyFields(
        {
            page: 1,
            pageSize: 10000,
            country: country.code,
            selectOnly: 'id,lat,lng,name,location',
            userNodeId,
        },
        {
            enabled: !!userNodeId,
        }
    );

    if (!fieldsQuery.data?.results || fieldsQuery.isValidating || fieldQuery.isValidating)
        return (
            <Stack justifyContent='center' alignItems='center' height={'100%'}>
                <Spinner />
            </Stack>
        );

    const fieldZoneIds = fieldQuery.data?.node?.zones.map(({ id }) => id).join('|');

    return (
        <CarbonCalculator
            fields={fieldsQuery.data.results}
            fieldZoneIds={fieldZoneIds}
            selectedFieldId={selectedFieldId}
            setSelectedFieldId={setSelectedFieldId}
            userNodeId={userNodeId}
        />
    );
};
export default CarbonCalculatorWrapper;
